import React, { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import "./admincontrols.css";

const InternshipApplications = () => {
  const [selected, setSelected] = useState([]);
  const [rejected, setRejected] = useState([]);
  const [ongoing, setOngoing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [slots, setSlots] = useState([]);

  const [openSelected, setOpenSelected] = useState(true);
  const [openRejected, setOpenRejected] = useState(false);
  const [openOngoing, setOpenOngoing] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [ongoingSelectedRows, setOngoingSelectedRows] = useState([]);

  // Search states
  const [searchSelected, setSearchSelected] = useState("");
  const [searchRejected, setSearchRejected] = useState("");
  const [searchOngoing, setSearchOngoing] = useState("");

  useEffect(() => {
    fetch("/config.json")
      .then((res) => res.json())
      .then((data) => setConfig(data))
      .catch((err) => console.error("Error loading config:", err));
  }, []);

  // Fetch applications
  useEffect(() => {
    if (!config) return;

    fetch(`${config.BASE_URL}${config.ONGOING_INTERNS}`, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          setSelected(data.selected);
          setRejected(data.rejected);
          setOngoing(data.ongoing);
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching applications:", err);
        setLoading(false);
      });
  }, [config]);

  // Fetch slots for mode
  useEffect(() => {
    if (!config) return;

    fetch(`${config.BASE_URL}${config.GET_SLOTS}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success) setSlots(data.slots);
      })
      .catch((err) => console.error("Error fetching slots:", err));
  }, [config]);

  if (loading) return <p>Loading applications...</p>;

  const filterData = (data, search) => {
    if (!search.trim()) return data;

    const term = search.toLowerCase();
    return data.filter((item) =>
      [
        item.intern_id,
        item.id,
        item.name,
        item.email,
        item.user_id,
        item.phone,
        item.domain,
        item.college,
        item.amount,
        item.duration,
        item.slot,
      ].some(
        (field) =>
          field &&
          field.toString().toLowerCase().includes(term)
      )
    );
  };

  const getModeForItem = (item) => {
    if (!slots || slots.length === 0) return "-";
    const slotInfo = slots.find((s) => s.slotno === item.slot);
    return slotInfo ? slotInfo.mode : "-";
  };

  const downloadExcel = (data, filename, dateField, title) => {
    const cleaned = data.map((item, index) => {
      const base = {
        "S.No": index + 1,
        "Application ID": item.application_id || item.id || "-",
        Name: item.name,
        "Mobile Number": item.phone,
        College: item.college,
        Domain: item.domain,
        Amount: item.amount,
        Duration: item.duration,
        Date: item[dateField] || "-",
        Status:
          title === "Ongoing Applications"
            ? "Ongoing"
            : item.reject_reason
            ? item.reject_reason
            : item.status
            ? "Confirmed"
            : "Pending",
        Slot: item.slot || "-",
      };

      if (title === "Ongoing Applications") {
        base["Intern ID"] = item.intern_id || "-";
        base["Mode"] = getModeForItem(item);
      } else {
        base["Email"] = item.email || item.user_id || "-";
      }

      return base;
    });

    const worksheet = XLSX.utils.json_to_sheet(cleaned);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, filename);
  };

  const renderTable = (
    title,
    data,
    dateField,
    search,
    setSearch,
    isOpen,
    setIsOpen,
    selectedRowsState,
    setSelectedRowsState
  ) => {
    const filteredData = filterData(data, search);

    const toggleRow = (id) => {
      setSelectedRowsState((prev) =>
        prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
      );
    };

    const closeInternships = async () => {
      if (title !== "Ongoing Applications") return;
      if (!selectedRowsState.length) {
        alert("Please select at least one internship to close.");
        return;
      }
      if (!window.confirm("Are you sure you want to mark the selected internships as completed?"))
        return;

      try {
        const res = await fetch(`${config.BASE_URL}${config.CLOSE_INTERNSHIPS}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ids: selectedRowsState, completed_status: 1 }),
        });
        const json = await res.json();
        if (!json.success) {
          alert(json.message || "Failed to update internships");
          return;
        }
        setOngoing(ongoing.filter((row) => !selectedRowsState.includes(row.intern_id)));
        setOngoingSelectedRows([]);
        alert("Selected internships marked as completed successfully!");
      } catch (err) {
        console.error(err);
        alert("Network error: Could not update internships");
      }
    };

    const updateAmount = async () => {
      if (title !== "Selected Applications") return;
      if (!selectedRowsState.length) {
        alert("Please select at least one application");
        return;
      }
      const amount = prompt("Enter new amount:");
      if (!amount || !amount.trim()) return alert("Amount cannot be empty");

      try {
        const res = await fetch(`${config.BASE_URL}${config.UPDATE_INTERNS_AMOUNT}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ids: selectedRowsState, amount }),
        });
        const json = await res.json();
        if (!json.success) return alert(json.message || "Failed to update in database");

        const updated = data.map((row) =>
          selectedRowsState.includes(row.application_id) ? { ...row, amount } : row
        );
        setSelected(updated);
        alert("Amount updated successfully!");
      } catch (err) {
        console.error(err);
        alert("Network error: Could not update amount");
      }
    };

    return (
      <div className="table-section">
        <div className="table-title-bar" onClick={() => setIsOpen(!isOpen)}>
          <h2>{title}</h2>
          <span className={`arrow ${isOpen ? "open" : ""}`}>▼</span>
        </div>

        <div
          className="table-content"
          style={{ maxHeight: isOpen ? "fit-content" : "0px", opacity: isOpen ? 1 : 0 }}
        >
          <div className="table-header-row">
            <input
              type="text"
              placeholder={`Search ${title}...`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="table-search-input"
            />

            {title === "Selected Applications" && (
              <button className="update-btn" onClick={updateAmount}>
                Update Amount
              </button>
            )}

            {title === "Ongoing Applications" && (
              <button className="update-btn" onClick={closeInternships}>
                Close Selected
              </button>
            )}

            <button
              className="table-excel-btn"
              onClick={() =>
                downloadExcel(filteredData, `${title.replace(/ /g, "_")}.xlsx`, dateField, title)
              }
            >
              Download Excel
            </button>
          </div>

          {filteredData.length === 0 ? (
            <p>No records found.</p>
          ) : (
            <table className="internship-table">
              <thead>
                <tr>
                  {(title === "Selected Applications" || title === "Ongoing Applications") && (
                    <th>Select</th>
                  )}
                  <th>S.No</th>
                  <th>Application ID</th>
                  {title === "Ongoing Applications" && <th>Intern ID</th>}
                  <th>Name</th>
                  {title !== "Ongoing Applications" && <th>Email</th>}
                  <th>Mobile</th>
                  <th>College</th>
                  <th>Domain</th>
                  <th>Amount</th>
                  <th>Duration</th>
                  <th>Applied At</th>
                  <th>Date</th>
                  <th>Status</th>
                  <th>Slot</th>
                  {title === "Ongoing Applications" && <th>Mode</th>}
                </tr>
              </thead>
              <tbody>
                {filteredData.map((item, idx) => (
                  <tr key={idx}>
                    {(title === "Selected Applications" || title === "Ongoing Applications") && (
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedRowsState.includes(item.application_id || item.intern_id)}
                          onChange={() => toggleRow(item.application_id || item.intern_id)}
                        />
                      </td>
                    )}

                    <td>{idx + 1}</td>
                    <td>{item.application_id || item.id}</td>
                    {title === "Ongoing Applications" && <td>{item.intern_id}</td>}
                    <td>{item.name}</td>
                    {title !== "Ongoing Applications" && <td>{item.email || item.user_id}</td>}
                    <td>{item.phone}</td>
                    <td>{item.college}</td>
                    <td>{item.domain}</td>
                    <td>{item.amount}</td>
                    <td>{item.duration}</td>
                    <td>
                    {item.submitted_at ? new Date(item.submitted_at).toLocaleDateString() : "-"}
                    </td>
                    <td>{item[dateField] || "-"}</td>
                    <td>
                      {title === "Ongoing Applications"
                        ? "Ongoing"
                        : item.reject_reason
                        ? item.reject_reason
                        : item.status
                        ? "Confirmed"
                        : "Pending"}
                    </td>
                    <td>{item.slot || "-"}</td>
                    {title === "Ongoing Applications" && <td>{getModeForItem(item)}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="internship-tables-page">
      {renderTable(
        "Selected Applications",
        selected,
        "selected_date",
        searchSelected,
        setSearchSelected,
        openSelected,
        setOpenSelected,
        selectedRows,
        setSelectedRows
      )}

      {renderTable(
        "Rejected Applications",
        rejected,
        "updated_at",
        searchRejected,
        setSearchRejected,
        openRejected,
        setOpenRejected,
        [],
        () => {}
      )}

      {renderTable(
        "Ongoing Applications",
        ongoing,
        "internship_start_date",
        searchOngoing,
        setSearchOngoing,
        openOngoing,
        setOpenOngoing,
        ongoingSelectedRows,
        setOngoingSelectedRows
      )}
    </div>
  );
};

export default InternshipApplications;
